import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Plus, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LoadingSpinner } from './loading';

export interface TypeaheadOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface TypeaheadProps {
  label?: string;
  required?: boolean;
  placeholder?: string;
  inputValue: string;
  onInputChange: (text: string) => void;
  onSelect: (option: TypeaheadOption) => void;
  onCreateNew: (text: string) => void;
  options: TypeaheadOption[];
  isLoading?: boolean;
  error?: string;
  warning?: string;
  maxLength?: number;
  allowCreate?: boolean;
  disabled?: boolean;
  selectedId?: string;
  /** Label for the inline-create row. Defaults to "Create new". e.g. "Add Building" renders as "+ Add Building: <typed text>". */
  createLabel?: string;
}

export function Typeahead({
  label,
  required,
  placeholder = 'Search...',
  inputValue,
  onInputChange,
  onSelect,
  onCreateNew,
  options,
  isLoading = false,
  error,
  warning,
  maxLength,
  allowCreate = true,
  disabled = false,
  selectedId,
  createLabel = 'Create new',
}: TypeaheadProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Position is computed against the input wrapper's bounding rect and pinned
  // to viewport coords so an overflow-hidden ancestor (e.g. Card's rounded
  // corners) cannot clip the dropdown.
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const trimmedInput = inputValue.trim();
  const canCreate =
    allowCreate &&
    trimmedInput.length > 0 &&
    (maxLength === undefined || trimmedInput.length <= maxLength);

  const showDropdown = isOpen && (isLoading || options.length > 0 || canCreate);

  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    // iOS Safari quirk: getBoundingClientRect() returns coordinates
    // relative to the *visual* viewport, but position:fixed is
    // anchored to the *layout* viewport. When the on-screen keyboard
    // opens the visual viewport scrolls (visualViewport.offsetTop > 0)
    // and the two coordinate systems drift apart — the dropdown ends
    // up where the input USED to be (above the input, separated by a
    // gap). Add the visual viewport's offset back in so the fixed-
    // positioned dropdown lands at the input's actual on-screen
    // location regardless of keyboard state. No-op on browsers
    // without visualViewport support.
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const offsetTop = vv?.offsetTop ?? 0;
    const offsetLeft = vv?.offsetLeft ?? 0;
    setDropdownPos({
      top: rect.bottom + 4 + offsetTop,
      left: rect.left + offsetLeft,
      width: rect.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!showDropdown) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true); // capture so nested scrollers also fire
    window.addEventListener('resize', updatePosition);
    // iOS Safari opens the on-screen keyboard by shrinking the visual
    // viewport and auto-scrolling the focused input into view. That
    // motion fires `visualViewport` events but NOT a regular document
    // `scroll`, so the portal's fixed-position coords go stale and the
    // dropdown ends up where the input *used to be* (above it on the
    // page, with the input now scrolled to where the dropdown should
    // have been). Track visualViewport too so the dropdown follows.
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (vv) {
      vv.addEventListener('scroll', updatePosition);
      vv.addEventListener('resize', updatePosition);
    }
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      if (vv) {
        vv.removeEventListener('scroll', updatePosition);
        vv.removeEventListener('resize', updatePosition);
      }
    };
  }, [showDropdown, updatePosition]);

  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      const inContainer = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inContainer && !inDropdown) {
        setIsOpen(false);
        setIsFocused(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    if (maxLength !== undefined && val.length > maxLength) return;
    onInputChange(val);
    setIsOpen(true);
  }

  function handleFocus() {
    setIsFocused(true);
    setIsOpen(true);
  }

  function handleSelect(option: TypeaheadOption) {
    if (option.disabled) return;
    onSelect(option);
    setIsOpen(false);
  }

  function handleCreateNew() {
    if (!canCreate) return;
    onCreateNew(trimmedInput);
    setIsOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          {label}
          {required && <span className="text-error-500 ml-0.5">*</span>}
        </label>
      )}

      <div
        className={cn(
          // Same iOS-auto-zoom rationale as the Input component: 16px
          // (text-base) on mobile keeps Safari from zooming in on focus
          // and never zooming back out; desktop uses the tighter 14px.
          'flex items-center w-full px-3 py-2 text-base sm:text-sm bg-white dark:bg-gray-800 border rounded-lg transition-colors',
          isFocused
            ? 'border-primary-500 ring-2 ring-primary-500 outline-none'
            : error
              ? 'border-error-500 hover:border-error-600'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500',
          disabled && 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-900'
        )}
      >
        <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mr-2" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          // Font-size MUST live on the input element itself (not just
          // the wrapper) — iOS Safari reads the input's *computed*
          // font-size to decide whether to auto-zoom on focus, and
          // <input> doesn't always inherit from the parent because of
          // UA stylesheets. text-base (16px) on mobile suppresses the
          // zoom; text-sm (14px) on sm+ where auto-zoom doesn't apply.
          className="flex-1 bg-transparent outline-none text-base sm:text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 disabled:cursor-not-allowed"
        />
        {isLoading && (
          <span className="ml-2 flex-shrink-0">
            <LoadingSpinner size="sm" />
          </span>
        )}
      </div>

      {error && <p className="mt-1.5 text-sm text-error-600">{error}</p>}

      {warning && (
        <div className="mt-2 flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg">
          <span className="text-amber-500 text-base leading-none mt-0.5">⚠</span>
          <p className="text-sm text-amber-800 dark:text-amber-300">{warning}</p>
        </div>
      )}

      {showDropdown && dropdownPos && createPortal(
        <div
          ref={dropdownRef}
          style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[1000] overflow-hidden"
        >
          <div className="max-h-52 overflow-y-auto">
            {isLoading && options.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-3 py-4 text-sm text-gray-400">
                <LoadingSpinner size="sm" />
                Searching...
              </div>
            ) : (
              <>
                {options.length === 0 && !canCreate && (
                  <div className="px-3 py-4 text-sm text-gray-400 text-center">
                    No results found
                  </div>
                )}

                {options.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option)}
                    disabled={option.disabled}
                    className={cn(
                      'w-full flex items-center justify-between px-3 py-2.5 text-sm text-left transition-colors',
                      option.disabled
                        ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-500'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100',
                      option.value === selectedId && 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    )}
                  >
                    <span className="truncate">{option.label}</span>
                    {option.value === selectedId && (
                      <Check className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400 flex-shrink-0 ml-2" />
                    )}
                  </button>
                ))}

                {canCreate && (
                  <button
                    type="button"
                    onClick={handleCreateNew}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-primary-700 dark:text-primary-300 font-medium hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors border-t border-gray-100 dark:border-gray-700"
                  >
                    <Plus className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>
                      {createLabel}:{' '}
                      <span className="font-semibold">{trimmedInput}</span>
                    </span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
